import { Link, useRouterState } from '@tanstack/react-router';
import { Building2, FileText, Gauge, History, LogOut, Receipt, ShieldCheck, Upload } from 'lucide-react';
// FileText is kept for the Documenten nav-icon; the DocR-logo replaces the placeholder icons in the header.
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { Me } from '@/lib/api';
import { cn, initials } from '@/lib/utils';

interface ShellProps {
	user: Me;
	children: ReactNode;
}

type NavItem = {
	to: string;
	label: string;
	icon: typeof Gauge;
	visible: (user: { role: string; can_view_invoices: boolean }) => boolean;
};

const NAV_ITEMS: NavItem[] = [
	{ to: '/', label: 'Dashboard', icon: Gauge, visible: () => true },
	{ to: '/documents', label: 'Documenten', icon: FileText, visible: () => true },
	{ to: '/upload', label: 'Uploaden', icon: Upload, visible: () => true },
	{ to: '/inkoopfacturen', label: 'Inkoopfacturen', icon: Receipt, visible: (u) => u.can_view_invoices },
	{ to: '/verkoopfacturen', label: 'Verkoopfacturen', icon: Receipt, visible: (u) => u.can_view_invoices },
	{ to: '/audit', label: 'Audit-trail', icon: History, visible: () => true },
	{ to: '/admin/entities', label: 'Entiteiten', icon: Building2, visible: (u) => u.role === 'admin' },
];

export function Shell({ user, children }: ShellProps) {
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	return (
		<div className="min-h-screen bg-muted/30">
			<aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r bg-background lg:flex">
				<div className="flex h-16 items-center border-b px-5">
					<img
						src="/docr-logo-400.png"
						alt="DocR"
						className="h-7 w-auto select-none"
						draggable={false}
					/>
				</div>

				<nav className="flex-1 space-y-1 p-3">
					{NAV_ITEMS.filter((i) => i.visible(user)).map((item) => {
						const active = pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to));
						const Icon = item.icon;
						return (
							<Link
								key={item.to}
								to={item.to}
								className={cn(
									'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
									active
										? 'bg-primary text-primary-foreground'
										: 'text-muted-foreground hover:bg-muted hover:text-foreground',
								)}
							>
								<Icon className="h-4 w-4" />
								{item.label}
							</Link>
						);
					})}
				</nav>

				<div className="border-t p-3">
					<div className="flex items-center gap-3 rounded-md px-3 py-2">
						<div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold">
							{initials(user.name)}
						</div>
						<div className="flex min-w-0 flex-col">
							<span className="truncate text-sm font-medium">{user.name}</span>
							<span className="truncate text-xs text-muted-foreground">{user.email}</span>
						</div>
					</div>
					{user.role === 'admin' && (
						<div className="mt-2 flex items-center gap-1.5 px-3 text-[11px] text-docr-teal">
							<ShieldCheck className="h-3 w-3" />
							admin - ziet alle entiteiten
						</div>
					)}
				</div>
			</aside>

			<div className="lg:pl-60">
				<header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b bg-background/80 px-6 backdrop-blur">
					<div className="flex items-center lg:hidden">
						<img
							src="/docr-logo-400.png"
							alt="DocR"
							className="h-6 w-auto select-none"
							draggable={false}
						/>
					</div>
					<div className="hidden lg:block" />
					<div className="flex items-center gap-3">
						<span className="hidden text-xs text-muted-foreground sm:inline">
							Omgeving: <span className="font-mono text-foreground">prod</span>
						</span>
						<Button variant="ghost" size="sm" disabled title="Uitloggen - beschikbaar in Phase 16">
							<LogOut className="mr-2 h-4 w-4" />
							<span className="hidden sm:inline">Uitloggen</span>
						</Button>
					</div>
				</header>

				<nav className="sticky top-16 z-10 flex gap-1 border-b bg-background/80 px-4 py-2 backdrop-blur lg:hidden">
					{NAV_ITEMS.map((item) => {
						const active = pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to));
						const Icon = item.icon;
						return (
							<Link
								key={item.to}
								to={item.to}
								className={cn(
									'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
									active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
								)}
							>
								<Icon className="h-3.5 w-3.5" />
								{item.label}
							</Link>
						);
					})}
				</nav>

				<main className="p-6">{children}</main>
			</div>
		</div>
	);
}
